import Layout from '@src/components/Layout';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, Icon, Statistic } from 'semantic-ui-react';
import MilkPaymentService from '~/api/services/milkPayment';
import { FormFields } from '~/components/FormFields';
import i18n from '~/components/i18n';
import InfoPopup from '~/components/InfoPopup';
import notify from '~/components/Notify';
import { formatNumber, getMonths } from '~/utils/helpers';
import { StyledLabelWrapper } from './style';
import ExcelExport from '~/components/ExcelExport';

const getRangeFields = () => [
  {
    key: 'monthFrom',
    label: i18n.t('Month from'),
    placeholder: i18n.t('Select month'),
    errorMessage: i18n.t('Please select month from')
  },
  {
    key: 'monthTo',
    label: i18n.t('Month to'),
    placeholder: i18n.t('Select month'),
    errorMessage: i18n.t('Please select month to')
  },
  {
    key: 'yearFrom',
    label: i18n.t('Year from'),
    placeholder: i18n.t('Select year'),
    errorMessage: i18n.t('Please select year from')
  },
  {
    key: 'yearTo',
    label: i18n.t('Year to'),
    placeholder: i18n.t('Select year'),
    errorMessage: i18n.t('Please select year to')
  }
]

const MilkPayment: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const rangeFields = getRangeFields()

  const [options, setOptions] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const [farmerGroups, setFarmerGroups] = useState([])
  const [loadingFarmerGroups, setLoadingFarmerGroups] = useState(false)
  const [loadingBillData, setLoadingBillData] = useState(false)
  const [billData, setBillData] = useState(null)
  const billRef = useRef<HTMLDivElement>(null)

  const { handleSubmit, control, watch, setValue, getValues, formState } = useForm({
    mode: 'onSubmit'
  })
  const [
    watchTypeField,
    watchMonthFrom,
    watchMonthTo,
    watchYearFrom,
    watchYearTo,
    watchYear,
    watchMonth,
    watchFarmerGroups
  ] = watch(['type', 'monthFrom', 'monthTo', 'yearFrom', 'yearTo', 'year', 'month', 'farmerGroups'])
  const months = getMonths()

  useEffect(() => {
    fetchData()
  }, [t])

  useEffect(() => {
    const { isSubmitted } = formState
    if (isSubmitted) {
      setBillData(null)
    }
  }, [watchFarmerGroups])

  useEffect(() => {
    const { isSubmitted, errors } = formState
    if (isSubmitted && Object.keys(errors).length > 0) {
      setBillData(null)
    }
  }, [formState.isSubmitted, formState.errors])

  useEffect(() => {
    const { isSubmitted } = formState
    if (isSubmitted) {
      setBillData(null)
    }
    if (watchTypeField && watchTypeField === 'month') {
      if (typeof (watchMonth) === 'number' && watchYear) {
        fetchFarmerGroups()
      }
    } else if (watchTypeField && watchTypeField === 'range') {
      if (typeof (watchMonthFrom) === 'number' && typeof (watchMonthTo) === 'number' && watchYearFrom && watchYearTo) {
        fetchFarmerGroups()
      }
    }
  }, [
    watchMonthFrom,
    watchMonthTo,
    watchYearFrom,
    watchYearTo,
    watchYear,
    watchMonth
  ])

  async function fetchFarmerGroups() {
    setLoadingFarmerGroups(true)
    try {
      const payload = getValues()
      delete payload.farmerGroups
      delete payload.formats
      delete payload.columns
      const response = await MilkPaymentService.getFarmerGroups(payload)
      setFarmerGroups(response.data?.map(group => ({ value: group.id, text: group.name })) || [])
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingFarmerGroups(false)
  }

  useEffect(() => {
    if (watchTypeField && watchTypeField === 'range') {
      setValue('year', '')
      setValue('month', '')
    }
    else if (watchTypeField === 'month') {
      rangeFields.map(rangeField => setValue(rangeField.key, ''))
    }
  }, [watchTypeField])

  async function fetchData() {
    setIsBusy(true)
    try {
      const responseOptions = await MilkPaymentService.getOptions()
      setValue('type', 'month')
      setOptions(responseOptions.data)
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function renderRange() {
    return <>
      {rangeFields.map(rangeField => {
        const selectOptions = rangeField.key.startsWith('year') ? options?.years.map(year => ({ value: year, text: year })) || [] :
          months.map((month, index) => ({ value: index + 1, text: month }))
        return <FormFields.Select
          key={rangeField.key}
          control={control}
          placeholder={rangeField.placeholder}
          label={rangeField.label}
          rules={{
            required: {
              value: true,
              message: rangeField.errorMessage
            }
          }}
          selectOnBlur={false}
          name={rangeField.key}
          options={selectOptions}
        />
      })}
    </>
  }

  function renderMonth() {
    return <>
      <FormFields.Select
        control={control}
        placeholder={t('Select month')}
        label={t('Month')}
        selectOnBlur={false}
        rules={{
          required: {
            value: true,
            message: t('Please select month')
          }
        }}
        name={'month'}
        options={months.map((month, index) => ({ value: index + 1, text: month }))}
      />
      <FormFields.Select
        control={control}
        placeholder={t('Select year')}
        label={t('Select year')}
        rules={{
          required: {
            value: true,
            message: t('Please select year')
          }
        }}
        selectOnBlur={false}
        name={'year'}
        options={options?.years.map(year => ({ value: year, text: year })) || []}
      />
    </>

  }

  function renderForm() {
    return <>
      <FormFields.Select
        control={control}
        placeholder={t('Select type')}
        label={t('Select type')}
        rules={{
          required: {
            value: true,
            message: t('Please select type')
          }
        }}
        selectOnBlur={false}
        name={'type'}
        options={options?.types.map(type => ({ value: type.key, text: type.label })) || []}
      />
      <Form.Group widths={'equal'}>
        {watchTypeField === 'range' ? renderRange() : renderMonth()}
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'farmerGroups'}
          rules={{
            required: {
              value: true,
              message: t('Please select farmer group')
            }
          }}
          multiple
          options={farmerGroups}
          disabled={loadingFarmerGroups}
          loading={loadingFarmerGroups}
          placeholder={t('Select farmer group')}
          label={<StyledLabelWrapper>
            <label>{t('Farmer groups')}</label>
            <span style={{ color: 'red' }}>*</span>
            <InfoPopup
              content={t('Select month and year to get farmer groups')}
              styleIcon={{ marginLeft: 3, cursor: 'pointer' }}
            />
          </StyledLabelWrapper>}
        />
        <FormFields.Select
          control={control}
          upward={true}
          placeholder={t('Select attributes')}
          label={t('Select attributes for export')}
          rules={{
            required: {
              value: true,
              message: t('Please select attributes')
            }
          }}
          multiple
          selectOnBlur={false}
          name={'columns'}
          options={options?.columns.map(type => ({ value: type.key, text: type.label })) || []}
        />
        <FormFields.Select
          control={control}
          upward={true}
          placeholder={t('Select export format')}
          label={t('Select export format')}
          rules={{
            required: {
              value: true,
              message: t('Please select export format')
            }
          }}
          selectOnBlur={false}
          name={'format'}
          options={options?.formats.map(type => ({ value: type.key, text: type.label })) || []}
        />
      </Form.Group>
    </>
  }

  async function onSubmit(data) {
    setLoadingBillData(true)
    try {
      const response = await MilkPaymentService.getExportInfo(data)
      setBillData(response.data)
      if (billRef.current) {
        billRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingBillData(false)
  }

  function renderBillData() {
    return <div ref={billRef} style={{ display: 'relative' }}>
      <Statistic.Group size='tiny' style={{ width: '50%', marginTop: '3em', marginLeft: '-18px', gap: 30 }}>
        <Statistic>
          <Statistic.Value>
            <Icon name='bars' />{billData?.totalRows}
          </Statistic.Value>
          <Statistic.Label>{t('Rows')}</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>
            <Icon name='euro sign' />{formatNumber(billData?.total)}
          </Statistic.Value>
          <Statistic.Label>{t('Total')}</Statistic.Label>
        </Statistic>
        <ExcelExport
          url={'milk-billing/export'}
          fileName={'bill'}
          name={t('Export now')}
          primary
          data={getValues()}
          buttonType={'button'}
        />
      </Statistic.Group>
    </div>
  }

  return <Layout
    isBusy={isBusy}
    onSubmit={handleSubmit(onSubmit)}
    useSpinner
    childrenOverflow={'hidden'}
    childContainerSize={'300px'}
    fontSizeHeader={'18px'}
    action={t('Export a bill')}
    title={t('Export a billing')}
  >
    {renderForm()}
    {billData && renderBillData()}
  </Layout>;
};

export default MilkPayment;
